import { z } from 'zod';
/**
 * Account invitations — setting a password on an account that has none usable.
 *
 * ⚠️ **WHY THIS EXISTS.** Every account-creating path on this platform minted a
 * random password and returned the plaintext exactly once. There was no reset
 * and no invitation, so an administrator who lost that string was RE-CREATED
 * rather than recovered — and any bulk creation of accounts produced one
 * unrecoverable secret per row with nowhere to put them.
 *
 * An invitation replaces the minted password with a single-use expiring link
 * sent to the address itself. The account exists and cannot be signed into until
 * somebody holding that link chooses a password.
 */
/**
 * What the accept page may say before anybody has proved anything.
 *
 * ⚠️ Served on an UNAUTHENTICATED route, so everything here is visible to
 * whoever holds the link — which is why the address is MASKED. The person who
 * actually received the email recognises their own masked address perfectly
 * well; a stranger who found the link learns neither the mailbox nor the
 * organisation.
 */
export const invitationSubjectSchema = z.object({
    /**
     * What they were invited to, for wording only.
     *
     * ⚠️ NEVER an authorisation input. What the account may do is decided by the
     * memberships it holds; this is a label somebody typed, shown so the reader
     * knows which invitation they are accepting.
     */
    context: z.string(),
    /** `j••••@•••.com` — see `maskEmail`. */
    maskedEmail: z.string(),
    displayName: z.string(),
    expiresAt: z.string(),
});
export const acceptInvitationRequestSchema = z
    .object({
    /**
     * Matches `loginRequestSchema.password` exactly.
     *
     * ⚠️ A stricter rule HERE than at login would refuse a password the login
     * route would later accept, which is a dead end nobody can diagnose; a
     * looser one would let an invitation set a credential the platform would
     * otherwise have refused. They must be the same rule, and today that rule
     * is a minimum length.
     */
    password: z.string().min(8),
})
    .strict();
/**
 * What happened when an invitation was sent.
 *
 * ⚠️ **Reports the SEND rather than claiming a delivery**, exactly as the
 * verification-code path does. A failure here does not fail the request: the
 * invitation is already written and still valid, so a 500 would throw away a
 * usable token and say nothing useful.
 *
 * ⚠️ `NOT_CONFIGURED` is the one that matters. The tenant has no mail server, so
 * the invitation cannot reach anybody — and an account nobody can sign into with
 * no way to send them a link is worse than the one-time password it replaced.
 * The caller must handle it rather than treat it as a warning.
 */
export const invitationDeliverySchema = z.enum(['SENT', 'NOT_CONFIGURED', 'FAILED']);
export const invitationIssuedSchema = z.object({
    delivery: invitationDeliverySchema,
    expiresAt: z.string(),
    /**
     * The link, returned ONCE and only when it could not be sent.
     *
     * ⚠️ **Present exactly when `delivery !== 'SENT'`.** This is the degradation
     * that keeps a mail-less deployment working at all: without it, an
     * unconfigured tenant creates administrators nobody can ever reach. It is
     * deliberately NOT returned on success — a link that arrived by email has no
     * business also being on a screen and in a browser history.
     *
     * ⚠️ And it is why a BULK import must check the mail server ONCE, before the
     * file, rather than discovering this per row: forty links on a screen is the
     * forty-passwords problem this whole mechanism exists to end.
     */
    acceptUrl: z.string().nullable(),
    /**
     * WHY it could not be sent — the mail provider's own words.
     *
     * ⚠️ **`FAILED` with no reason is a finding about our error handling, not about the
     * mail server.** The transport already returns the provider's message — *"535
     * authentication failed"*, *"connection timeout"*, *"sender not verified"* — and the
     * issuer discarded it, so an operator configuring a mail server got one word and
     * nowhere to go. The pipeline-health page records exactly this lesson: *"a failure
     * with no message says so"*.
     *
     * ⚠️ Truncated, and NULL on success — a reason on a message that arrived is noise, and
     * "nothing to say" and "said nothing" must stay apart.
     */
    deliveryError: z.string().nullable().default(null),
});
/** A live invitation, so a screen can say one is outstanding. */
export const pendingInvitationSchema = z.object({
    issuedAt: z.string(),
    expiresAt: z.string(),
    context: z.string(),
});
//# sourceMappingURL=invitation.js.map